'use client'

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import './podium.css';
import Loading from '../utils/loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faFire, faBolt, faCaretUp, faCaretDown, faAward } from '@fortawesome/free-solid-svg-icons';
import GlassCard from '@/components/shared/GlassCard';
import PlayerAvatar from '@/components/shared/PlayerAvatar';
import { trackEvent } from '@/components/utils/analytics';

const Podium = ({ podium }) => {
  const router = useRouter();

  if (podium === null) {
    return (
      <div className="podium-area">
        <Loading />
      </div>
    );
  }

  const top3 = (podium || []).filter((p) => p.rank >= 1 && p.rank <= 3);
  const first = top3.find((p) => parseInt(p.rank) === 1);
  const second = top3.find((p) => parseInt(p.rank) === 2);
  const third = top3.find((p) => parseInt(p.rank) === 3);

  if (!first && !second && !third) {
    return null;
  }

  const getPlayerKey = (result) => {
    if (!result) return '';
    return (!result.playerkey || result.playerkey === 'undefined' || result.playerkey.length === 0)
      ? result.rank + result.slackuid
      : result.playerkey;
  };

  const PodiumCard = ({ player, rankNum, order }) => {
    if (!player) return null;
    const isFirst = rankNum === 1;
    const avatarSize = isFirst ? 'xl' : 'lg';
    const playerKey = getPlayerKey(player);

    const rankLabels = { 1: '1', 2: '2', 3: '3' };

    const handleRecapClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      trackEvent('view_player_recap', { player_id: playerKey, player_name: player.name, source: 'podium' });
      router.push(`/recap/${playerKey}`);
    };

    return (
      <div className={`podium-card-wrapper podium-card-wrapper--${rankNum}`} style={{ order }}>
        <Link href={`/user/${playerKey}`} className="podium-card-link" onClick={() => trackEvent('view_player_profile', { player_id: playerKey, player_name: player.name, source: 'podium' })}>
          <GlassCard variant="elevated" className={`podium-card podium-card--${rankNum}`}>
            {/* Report-card link — propagation-stopped so the outer Link
                stays on /user/[id] for everything else on the card. */}
            <button
              type="button"
              className="podium-card__recap"
              onClick={handleRecapClick}
              aria-label={`See ${player.name}'s season report card`}
              title="Season report card"
            >
              <FontAwesomeIcon icon={faAward} />
            </button>
            <span className="podium-card__rank-watermark">{rankLabels[rankNum]}</span>

            <div className="podium-card__avatar">
              <PlayerAvatar
                name={player.name}
                imageurl={player.imageurl}
                size={avatarSize}
                showRank={false}
                rank={rankNum}
              />
            </div>

            <div className="podium-card__name">
              {player.name}
              {player.rankChange != null && player.rankChange !== 0 && (
                <span className={`rank-change rank-change--${player.rankChange > 0 ? 'up' : 'down'}`}>
                  <FontAwesomeIcon icon={player.rankChange > 0 ? faCaretUp : faCaretDown} />{Math.abs(player.rankChange)}
                </span>
              )}
            </div>

            {player.last5 && (
              <div className="podium-card__form">
                {player.last5.split(',').map((r, i) => (
                  <span key={i} className={`form-pip form-pip--${r.toLowerCase()}`} title={r.toUpperCase() === 'W' ? 'Win' : 'Loss'} />
                ))}
              </div>
            )}

            <div className="podium-card__points">
              <span className="podium-card__points-number">{player.totalpoints}</span>
              <span className="podium-card__points-label">pts</span>
            </div>

            <div className="podium-card__stats">
              <div className="podium-card__stat">
                <span className="podium-card__stat-value">{player.matchpoints ? parseFloat(player.matchpoints).toFixed(1) : 0}</span>
                <span className="podium-card__stat-label"><FontAwesomeIcon icon={faTrophy} /> Won</span>
              </div>
              <div className="podium-card__stat">
                <span className="podium-card__stat-value podium-card__stat-value--bonus">{player.bonuspoints ? parseFloat(player.bonuspoints).toFixed(1) : 0}</span>
                <span className="podium-card__stat-label">Bonus</span>
              </div>
              <div className="podium-card__stat">
                <span className="podium-card__stat-value podium-card__stat-value--accuracy">{player.accuracy || 0}%</span>
                <span className="podium-card__stat-label"><FontAwesomeIcon icon={faBolt} /> Acc</span>
              </div>
            </div>
          </GlassCard>
        </Link>
      </div>
    );
  };

  return (
    <div className="podium-area">
      <div className="podium-cards">
        <PodiumCard player={second} rankNum={2} order={1} />
        <PodiumCard player={first} rankNum={1} order={2} />
        <PodiumCard player={third} rankNum={3} order={3} />
      </div>
    </div>
  );
};

export default Podium;
